// Billing-related constants shared across billing pages. Kept here (rather than
// colocated with a single page component) so two independently-routed pages can
// read the same data without one importing from the other.

// Part number → human-readable description. Used by the "Part Number
// Description" KPI card on both the team-scoped deployments view
// (`PartNumberTracking`) and the company-wide deployments view
// (`CompanyPartNumberDeployments`). Consumers fall back to "-" when a
// part number isn't in this map (e.g. a newly-introduced SKU that the
// page hasn't been taught about yet).
export const PART_NUMBER_DESCRIPTIONS = {
  'PA-VTE-CORE-BASE-001':
    'Virtual Test Environment (VTE), Platform base fee that covers maintenance and support of the platform service. Monthly Subscription, Per Airline',
  'PA-VTE-VLRU-S-001':
    'Virtual LRU Small, 4 CPU Cores, 8 GB of RAM, 150 GB storage included. Per virtual LRU per Month per VTE',
  'PA-VTE-VLRU-M-001':
    'Virtual LRU Medium, 8 CPU Cores, 32 GB of RAM, 150 GB storage included. Per virtual LRU per Month per VTE',
  'PA-VTE-VLRU-L-001':
    'Virtual LRU Large, 12 CPU Cores, 32 GB of RAM, 150 GB storage included. Per virtual LRU per Month per VTE',
  'PA-VTE-VLRU-X-001':
    'Virtual LRU X-Large, 16 CPU Cores, 128 GB of RAM, 150 GB storage included. Per virtual LRU per Month per VTE',
  'PA-VTE-MEDIA-S-001':
    'Virtual LRU Storage Expansion, Media Tier Small, per 250 GB of storage. Per LRU per month per VTE',
  'PA-VTE-MEDIA-M-001':
    'Virtual LRU Storage Expansion, Media Tier Medium, per 500 GB of storage. Per LRU per month per VTE',
  'PA-VTE-MEDIA-L-001':
    'Virtual LRU Storage Expansion, Media Tier Large, per 1 TB of storage. Per LRU per Month per VTE',
  'PA-VTE-VLRU-C-YYYY':
    'Custom Virtual LRU, Virtual LRUs that deviate from standard profiles. Requires custom quote based on customer requirements. Per virtual LRU per Month per VTE'
};

// --- Deployment treatment + table shared between billing pages ---
//
// The team-scoped deployments view (`PartNumberTracking`) and the
// company-wide deployments view (`CompanyPartNumberDeployments`) render the
// same treatment buckets with the same chip styling and the same column
// order. Keep those rules in one place so a new bucket, a column rename, or
// a date-format tweak only has to land in one file.

// Buckets the API deployment can fall into. Order matters for `deriveTreatment`:
// `billable_quantity === 0` wins over decommissioned (a deployment with no
// remaining billable quantity has been *replaced*, regardless of its
// decommission date).
export const TREATMENTS = {
  BILLABLE_ACTIVE: 'Billable - Active',
  BILLABLE_DELETED: 'Billable - Deleted',
  EXCLUDED_REPLACED: 'Excluded - Replaced'
};

// Maps the derived treatment buckets to MUI Chip colors. Add a new entry
// here when introducing a new bucket — the chip will pick it up
// automatically.
export const TREATMENT_COLORS = {
  [TREATMENTS.BILLABLE_ACTIVE]: { color: 'primary', variant: 'outlined' },
  [TREATMENTS.BILLABLE_DELETED]: { color: 'warning', variant: 'outlined' },
  [TREATMENTS.EXCLUDED_REPLACED]: { color: 'default', variant: 'outlined' }
};

// Format an ISO date (or any Date-parseable value) for the deployments
// table. Falls back to "N/A" for null/undefined so missing `provision_date`
// or `decommission_date` values render cleanly.
export function formatDate(value) {
  if (!value) return 'N/A';
  const date = new Date(value);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

// Derive the treatment bucket from the deployment fields the API actually
// returns. The old mock data had an explicit `treatment` string; the API
// gives us the pieces (`billable_quantity`, `decommission_date`) instead.
export function deriveTreatment(deployment) {
  const billable = deployment.billable_quantity ?? 0;
  const decommissioned = deployment.decommission_date != null;
  if (billable === 0) return TREATMENTS.EXCLUDED_REPLACED;
  if (decommissioned) return TREATMENTS.BILLABLE_DELETED;
  return TREATMENTS.BILLABLE_ACTIVE;
}

// Map a single API deployment into the shared UI row shape used by both
// deployment tables. Returns only the fields every page renders; callers
// (e.g. the company-wide view, which threads `teamName` through every row)
// spread additional fields on top.
export function toBaseDeploymentRow(deployment) {
  return {
    id: `${deployment.vrack_name}-${deployment.provision_date}`,
    vrackId: deployment.vrack_name,
    quantity: deployment.quantity ?? 0,
    billableQuantity: deployment.billable_quantity ?? 0,
    treatment: deriveTreatment(deployment),
    owner: deployment.owner,
    createdDate: deployment.provision_date,
    deletedDate: deployment.decommission_date
  };
}

// Shared columns for the deployments table. The company-wide view prepends
// a `team` column; the team-scoped view uses these verbatim. Order here is
// the column order in the rendered table.
export const DEPLOYMENT_TABLE_COLUMNS = [
  { id: 'vrack', label: 'VRack', alignRight: false },
  { id: 'quantity', label: 'Quantity', alignRight: false },
  { id: 'billableQuantity', label: 'Billable Quantity', alignRight: false },
  { id: 'owner', label: 'Owner', alignRight: false },
  { id: 'createdDate', label: 'Created Date', alignRight: false },
  { id: 'deletedDate', label: 'Deleted Date', alignRight: false }
];
